import React from "react";
import { formatNumbersWithDecimal } from "../../components/formatNumbersWIthDecimal";

function ViewSalesModal({ setViewModal, item, currency }) {
  if (!item) return null;

  // Date-time formatting
  const formatDateTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Status badge color
  const getStatusColor = (status) => {
    switch ((status || "").toUpperCase()) {
      case "COMPLETED":
        return "bg-green-100 text-green-800 border-green-300";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "CANCELLED":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-blue-100 text-blue-800 border-blue-300";
    }
  };

  const loyalty = item?.loyalties?.[0];

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-gradient-to-br from-black/70 via-black/60 to-gray-900/70 animate-fadeIn">
      <div className="bg-white shadow-2xl rounded-2xl w-full max-w-3xl mx-4 max-h-[95vh] overflow-y-auto border border-gray-200 relative animate-slideUp">
        {/* Close Button */}
        <button
          onClick={() => setViewModal(false)}
          className="absolute top-4 right-4 text-3xl text-gray-400 hover:text-red-500 transition-all"
          title="Close"
        >
          ×
        </button>
        {/* Modal Header */}
        <div className="px-8 pt-8 pb-6 border-b border-gray-200 flex flex-col gap-1">
          <span className="flex gap-2 items-center text-blue-600 font-semibold uppercase tracking-wider text-xs">
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2" fill="#2563eb" /><path d="M7 10.5V9a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/><circle cx="9" cy="15" r="1" fill="#fff"/><circle cx="15" cy="15" r="1" fill="#fff"/></svg>
            Order No:
            <span className="text-gray-900">{item.orderNO}</span>
          </span>
          <h2 className="text-2xl font-bold text-gray-800 tracking-tight">Sales Details</h2>
          <div className="flex items-center gap-2 mt-2">
            <span className={`px-3 py-1 rounded-full border text-xs drop-shadow-sm ${getStatusColor(item.status)}`}>
              {item.status}
            </span>
            <span className="text-gray-500 text-xs">Date: {formatDateTime(item.updated_at)}</span>
          </div>
        </div>

        {/* Order, Customer & Cashier Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 px-8 py-6 bg-gray-50">
          {/* Order Info */}
          <div className="space-y-2">
            <div className="font-semibold text-sm text-blue-900 mb-1">Order</div>
            <div className="text-[15px]"><span className="font-medium text-gray-600">Type:</span> {item.payment_type}</div>
            <div className="text-[15px]"><span className="font-medium text-gray-600">Total:</span> <span className="font-bold text-blue-800">{currency}{formatNumbersWithDecimal(item.total_price)}</span></div>
          </div>
          {/* Customer Info */}
          <div className="space-y-2 border-l border-gray-200 pl-6">
            <div className="font-semibold text-sm text-blue-900 mb-1">Customer</div>
            <div className="text-[15px]"><span className="font-medium text-gray-600">Name:</span> {item.customer?.name}</div>
            <div className="text-[15px]"><span className="font-medium text-gray-600">Address:</span> {item.customer?.address || <span className="text-gray-400 italic">N/A</span>}</div>
          </div>
          {/* Cashier Info */}
          <div className="space-y-2 border-l border-gray-200 pl-6">
            <div className="font-semibold text-sm text-blue-900 mb-1">Cashier</div>
            <div className="text-[15px]"><span className="font-medium text-gray-600">Name:</span> {item.user?.firstname} {item.user?.lastname}</div>
            <div className="text-[15px]"><span className="font-medium text-gray-600">Email:</span> <a href={`mailto:${item.user?.email}`} className="text-blue-700 hover:underline">{item.user?.email}</a></div>
            <div className="text-[15px]"><span className="font-medium text-gray-600">Phone:</span> <a href={`tel:${item.user?.phoneno}`} className="text-blue-700 hover:underline">{item.user?.phoneno}</a></div>
          </div>
        </div>

        {/* Order Details Table */}
        <div className="px-8 py-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><rect width="24" height="24" rx="4" fill="#1d4ed8"/><path d="M7 12.5V9a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v3.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/><circle cx="9" cy="16" r="1.5" fill="#fff"/><circle cx="15" cy="16" r="1.5" fill="#fff"/></svg>
            Order Items
          </h3>
          <div className="overflow-auto rounded-md bg-white border border-gray-200">
            <table className="min-w-full text-[15px]">
              <thead>
                <tr className="bg-blue-50 text-blue-900">
                  <th className="py-2.5 px-4 font-medium border-b border-blue-200">Product</th>
                  <th className="py-2.5 px-4 font-medium border-b border-blue-200 text-center">Quantity</th>
                  <th className="py-2.5 px-4 font-medium border-b border-blue-200 text-right">Unit Price</th>
                  <th className="py-2.5 px-4 font-medium border-b border-blue-200 text-right">Line Total</th>
                </tr>
              </thead>
              <tbody>
                {item?.orderdetails?.map((order, i) => (
                  <tr key={order?.id || i} className="hover:bg-blue-50">
                    <td className="py-2.5 px-4">{order?.product_name}</td>
                    <td className="py-2.5 px-4 text-center">{order?.quantity_ordered}</td>
                    <td className="py-2.5 px-4 text-right">{currency}{formatNumbersWithDecimal(order?.unit_price)}</td>
                    <td className="py-2.5 px-4 text-right">
                      {currency}
                      {formatNumbersWithDecimal(
                        parseFloat(order?.quantity_ordered) * parseFloat(order?.unit_price)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-blue-100">
                  <td colSpan={2} />
                  <td className="py-2.5 px-4 font-semibold text-right">Total:</td>
                  <td className="py-2.5 px-4 font-bold text-blue-900 text-right">
                    {currency}{formatNumbersWithDecimal(item?.total_price)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Bills Section */}
        {item.bills && item.bills.length > 0 && (
          <div className="px-8 py-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#0ea5e9"/><path d="M8 12h8M8 16h8M16 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/></svg>
              Bills
            </h3>
            <div className="overflow-auto rounded-md bg-white border border-gray-200">
              <table className="min-w-full text-[15px]">
                <thead>
                  <tr className="bg-blue-50 text-blue-900">
                    <th className="py-2.5 px-4 font-medium border-b border-blue-200">Bill Name</th>
                    <th className="py-2.5 px-4 font-medium border-b border-blue-200 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {item.bills.map((bill, b) => (
                    <tr key={bill?.id || b} className="hover:bg-blue-50">
                      <td className="py-2.5 px-4">{bill?.bill_name}</td>
                      <td className="py-2.5 px-4 text-right">
                        {currency}{formatNumbersWithDecimal(bill?.auto_bill_total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-blue-100">
                    <td className="py-2.5 px-4 font-semibold text-right">Bills Total:</td>
                    <td className="py-2.5 px-4 font-bold text-blue-900 text-right">
                      {currency}
                      {formatNumbersWithDecimal(
                        item.bills.reduce(
                          (acc, bill) => acc + parseFloat(bill.auto_bill_total || 0),
                          0
                        )
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Loyalty Row */}
        <div className="px-8 pb-4">
          <div className="flex flex-wrap justify-between items-center gap-4">
            <div className="flex gap-3 items-center">
              <span className="text-gray-700 font-medium">Payment Method:</span>
              <span className="font-semibold">{item?.payment_type}</span>
            </div>
            <div className="flex gap-3 items-center">
              <span className="text-gray-700 font-medium">
                {loyalty?.name || "No Loyalty Applied"}
              :</span>
              <span className="font-semibold">
                {loyalty?.amount
                  ? `${currency}${formatNumbersWithDecimal(loyalty.amount)}`
                  : "-"}
              </span>
            </div>
            <div className="flex gap-3 items-center">
              <span className="text-gray-700 font-medium">Total Price:</span>
              <span className="font-bold text-blue-900">{currency}{formatNumbersWithDecimal(item?.total_price)}</span>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end items-center px-8 pb-8 pt-2 space-x-3">
          <button
            onClick={() => setViewModal(false)}
            className="text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full shadow transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ViewSalesModal;
